// Espace de configuration externe (lien magique) : un contact hors Wilout configure ici les
// différents formulaires (= types d'accréditation) de son projet, récupère un lien public par
// formulaire à diffuser, et valide ou refuse chaque demande reçue avant l'export Wilout (seules
// les demandes approuvées sortent dans le CSV, cf. api-operational routes/accreditations.js).
// Aucune authentification Wilout — l'accès repose uniquement sur le token de config transmis
// dans l'URL (?token=...).
//
// Habillage repris du design importé (claude.ai/design, "Formulaires Accreditation.dc.html") :
// bandeau dégradé, bandeau de KPI, cartes de formulaire avec icône "catégorie", liste de
// demandes en style avatar-initiales. La sélection multiple + validation/refus en masse, le
// regroupement par jour/soumission groupée, les champs personnalisés et l'historique des
// exports sont des ajouts par rapport à la maquette (pas contradictoires, juste plus outillés).
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import Papa from 'papaparse';
import {
  Container, Box, Paper, Stack, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, Button, IconButton, TextField, Collapse, Alert, CircularProgress,
  Tooltip, Checkbox, InputAdornment, MenuItem, Menu, Popover, ToggleButtonGroup, ToggleButton, Select,
  FormControl, InputLabel, Tabs, Tab, Dialog, DialogContent, DialogTitle,
} from '@mui/material';
import {
  RefreshCw, Plus, X, ChevronDown, ChevronRight, Check, Search, FileDown, Pencil, Link2, MoreVertical, Lock,
} from 'lucide-react';
import {
  getAccreditationConfigSession,
  updateAccreditationProjectEventDays,
  updateAccreditationAccessTypes,
  createAccreditationForm,
  updateAccreditationForm,
  closeAccreditationForm,
  getAccreditationFormResponses,
  regenerateAccreditationFormPublicLink,
  updateAccreditationResponseStatus,
  bulkUpdateAccreditationResponseStatus,
  logAccreditationExport,
  getAccreditationExportLog,
  getAccreditationWiloutExport,
} from '../../api/api';
import AccreditationFormEditor from './AccreditationFormEditor';
import AccreditationSettingsAside from './AccreditationSettingsAside';
import AccreditationAccessCodesPanel from './AccreditationAccessCodesPanel';
import useAutosave from './useAutosave';
import { eventDatesLabel } from './eventDates';
import wiloutLogo from '../../images/wilout-logo-full.svg';
import { categoryVisual, STATUS_META, BRAND_GRADIENT } from './formVisuals';

const ExternalAccreditationTools = lazy(() => import('./ExternalAccreditationTools'));

const TOOLS_FALLBACK = <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

// Onglets « outils fichiers » (convertisseur, QR codes en réserve), communs à l'espace complet et
// à l'espace « convertisseur seul ».
const TOOL_VIEWS = [
  { view: 'converter', label: 'Convertir un fichier' },
  { view: 'spares-view', label: 'QR codes' },
  { view: 'access-codes', label: 'Infos accès' }, // codes d'accès de l'event relié (lecture seule)
  { view: 'spares-generate', label: 'Générer des QR codes' }, // en dernier, sous code d'accès
];
const SPARES_VIEW_INDEX = TOOL_VIEWS.findIndex((v) => v.view === 'spares-view');
const FORM_TAB_COUNT = 3; // Formulaires, Toutes les réponses, Historique des exports

const publicFormUrl = (token) => `${window.location.origin}/accreditation-form/${token}`;

const initialsOf = (nom, prenom) => `${(prenom || '')[0] || ''}${(nom || '')[0] || ''}`.toUpperCase() || '?';

const EXPORT_FILTERS = [
  { value: 'all', label: 'Toutes' },
  { value: 'decided', label: 'Décidées (validées + refusées)' },
  { value: 'approved', label: 'Validées' },
  { value: 'rejected', label: 'Refusées' },
];
// Libellés courts des filtres d'export pour le sélecteur du menu « Exporter ».
const EXPORT_FILTER_SHORT = { all: 'Toutes', decided: 'Décidées', approved: 'Validées', rejected: 'Refusées' };

const STATUS_FILTERS = [{ value: 'all', label: 'Tous' }, { value: 'active', label: 'Actifs' }, { value: 'closed', label: 'Clôturés' }];

// Colonnes de la liste des formulaires : nom, statut, demandes, actions (lien de partage).
const FORM_ROW_COLUMNS = 'minmax(0, 1fr) 110px 150px auto';

const GUIDE_STEPS = [
  { title: 'Configurer', text: "Jours, types d'accès, puis un formulaire par profil (bénévole, presse, VIP…)." },
  { title: 'Partager le lien', text: 'Envoyez le lien du formulaire aux personnes ou chefs de pôle concernés.' },
  { title: 'Valider et importer', text: 'Validez les demandes, puis exportez au format Wilout Online.' },
];
const GUIDE_STORAGE_KEY = 'accreditation-config-guide-hidden';

const EXPORT_FILTER_TEST = {
  all: () => true,
  decided: (r) => r.status === 'approved' || r.status === 'rejected',
  approved: (r) => r.status === 'approved',
  rejected: (r) => r.status === 'rejected',
};

// L'export "format Wilout" journalise son mode préfixé `wilout:` (ex. `wilout:approved`) pour se
// distinguer de l'export brut dans l'historique (cf. handleExportWilout) — cette fonction
// retrouve le libellé du filtre de statut correspondant dans les deux cas.
const exportModeLabel = (mode) => {
  const isWilout = typeof mode === 'string' && mode.startsWith('wilout:');
  const filterValue = isWilout ? mode.slice('wilout:'.length) : mode;
  const filterLabel = EXPORT_FILTERS.find((f) => f.value === filterValue)?.label || filterValue;
  return isWilout ? `Format Wilout — ${filterLabel}` : filterLabel;
};

// Jour de soumission en heure LOCALE (pas UTC — une soumission tard le soir en France
// basculerait sinon sur le jour suivant avec toISOString().slice(0,10)).
const dayKeyOf = (dateStr) => {
  if (!dateStr) return 'inconnue';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'inconnue';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dayLabelOf = (dayKey) => {
  if (dayKey === 'inconnue') return 'Date inconnue';
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

// Valeurs cashless effectives d'une demande (formulaire « pôle »), pour celui qui approuve.
const CASHLESS_SOURCE_LABEL = { person: 'saisi par le chef de pôle', type: 'règle du type', form: 'règle du formulaire' };
const cashlessSummary = (c) => {
  if (!c) return '';
  const parts = [];
  if (c.credit) parts.push(`Crédits ${c.credit}`);
  ['A', 'B', 'C', 'D'].forEach((k) => { if (c[k]) parts.push(`${k} ${c[k]}`); });
  return `${parts.join(' · ') || 'Aucun cashless'} (${CASHLESS_SOURCE_LABEL[c.source] || c.source})`;
};

// Regroupe les réponses par jour de soumission puis, au sein d'un même jour, par groupId
// (personnes soumises ensemble dans le même envoi, cf. routes/accreditationPublicForm.js) — une
// réponse sans groupId (donnée antérieure à ce champ) forme son propre groupe solo.
const groupResponsesByDay = (responses) => {
  const sorted = [...responses].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  const dayMap = new Map();
  for (const r of sorted) {
    const dayKey = dayKeyOf(r.submittedAt);
    if (!dayMap.has(dayKey)) dayMap.set(dayKey, new Map());
    const groupMap = dayMap.get(dayKey);
    const groupKey = r.groupId || r.id;
    if (!groupMap.has(groupKey)) groupMap.set(groupKey, []);
    groupMap.get(groupKey).push(r);
  }
  return [...dayMap.entries()].map(([dayKey, groupMap]) => ({ dayKey, groups: [...groupMap.values()] }));
};

const AccreditationConfigApp = () => {
  const params = new URLSearchParams(window.location.search);
  const configToken = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const [responsesByForm, setResponsesByForm] = useState({});
  const [expandedFormId, setExpandedFormId] = useState('');
  const [decidingId, setDecidingId] = useState('');
  const [editingForm, setEditingForm] = useState(null); // null | {} (nouveau) | form existant
  const [saving, setSaving] = useState(false);
  const [copiedFormId, setCopiedFormId] = useState('');
  const [query, setQuery] = useState('');
  // 0 = Formulaires, 1 = Toutes les réponses, 2 = Historique des exports, puis TOOL_VIEWS
  // (3 = Convertir un fichier, 4 = Générer des QR codes, 5 = QR codes).
  const [activeTab, setActiveTab] = useState(0);
  // Espace « convertisseur seul » : onglet d'outil affiché (cf. TOOL_VIEWS).
  const [soloView, setSoloView] = useState('converter');
  const [exportLog, setExportLog] = useState([]);
  const [exportStatusFilter, setExportStatusFilter] = useState('all');
  // Brouillons locaux des jours de l'événement et des types d'accès (formulaires « chef de pôle »),
  // resynchronisés depuis la session à chaque chargement et enregistrés automatiquement (useAutosave).
  // La `key` d'un type est conservée pour qu'un renommage ne casse aucune demande.
  const [eventDaysDraft, setEventDaysDraft] = useState([]);
  const [accessTypesDraft, setAccessTypesDraft] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [formMenu, setFormMenu] = useState(null); // { anchorEl, formId }
  const [exportAnchor, setExportAnchor] = useState(null);
  const [guideHidden, setGuideHidden] = useState(() => {
    try { return window.localStorage.getItem(GUIDE_STORAGE_KEY) === '1'; } catch { return false; }
  });
  // Sélection multiple pour la validation/refus en masse — une sélection par formulaire
  // ({ [formId]: string[] }), remise à zéro à chaque rechargement (cf. load()).
  const [selectedByForm, setSelectedByForm] = useState({});
  const [bulkDecidingFormId, setBulkDecidingFormId] = useState('');
  const [exportingAll, setExportingAll] = useState(false);
  const [exportingWilout, setExportingWilout] = useState(false);
  // Boîte de dialogue "tout / nouveautés seulement" ouverte au clic sur un bouton d'export —
  // null quand fermée, sinon le type d'export à lancer une fois le choix fait.
  const [exportScopeChoice, setExportScopeChoice] = useState(null); // null | 'raw' | 'wilout'
  // Onglet "Toutes les réponses" : vue à plat tous formulaires confondus, pour valider/refuser
  // vite sans dérouler formulaire par formulaire. Sélection dédiée ({ formId, id }[]) car une
  // sélection ici peut mélanger plusieurs formulaires, contrairement à selectedByForm.
  const [globalQuery, setGlobalQuery] = useState('');
  const [globalStatusFilter, setGlobalStatusFilter] = useState('all');
  const [globalSelected, setGlobalSelected] = useState([]);
  const [globalBulkDeciding, setGlobalBulkDeciding] = useState(false);

  const load = useCallback(async () => {
    if (!configToken) {
      setError('Lien invalide : token manquant.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getAccreditationConfigSession(configToken);
      setSession(data);
      setEventDaysDraft(data.eventDays || []);
      setAccessTypesDraft(data.accessTypes || []);
      const entries = {};
      await Promise.all((data.forms || []).map(async (f) => {
        try {
          const res = await getAccreditationFormResponses(configToken, f.id);
          entries[f.id] = Array.isArray(res?.items) ? res.items : [];
        } catch {
          entries[f.id] = [];
        }
      }));
      setResponsesByForm(entries);
      setSelectedByForm({});
      // Espace « convertisseur seul » : pas d'historique d'exports de réponses (route refusée).
      if (data.formsEnabled === false) return;
      try {
        const log = await getAccreditationExportLog(configToken);
        setExportLog(Array.isArray(log?.items) ? log.items : []);
      } catch {
        setExportLog([]);
      }
    } catch (err) {
      setError(err?.response?.status === 404
        ? 'Lien invalide ou expiré. Contactez Wilout pour en obtenir un nouveau.'
        : 'Erreur lors du chargement de votre espace.');
    } finally {
      setLoading(false);
    }
  }, [configToken]);

  useEffect(() => { load(); }, [load]);

  // AccreditationFormEditor renvoie un objet plat (label + champs de config au même niveau) —
  // le backend attend `{ label, config }` (routes/accreditationExternal.js), d'où le
  // remodelage ici. zoneLabel reprend toujours le libellé courant (pas de champ dédié dans
  // l'éditeur) : envoyer '' écraserait le défaut serveur (`sanitizeConfig`) et viderait le nom
  // de tarif généré.
  const handleSaveForm = async (formData) => {
    const {
      label, mode, welcomeTitle, welcomeText, fullDuration, eventDays, barcodeLength, tarifPrefix,
      rules, customFields, poleCashless,
    } = formData;
    const payload = {
      label,
      mode,
      config: {
        zoneLabel: label, welcomeTitle, welcomeText, fullDuration, eventDays, barcodeLength,
        tarifPrefix, rules, customFields, poleCashless,
      },
    };
    setSaving(true);
    try {
      if (editingForm?.id) {
        await updateAccreditationForm(configToken, editingForm.id, payload);
      } else {
        await createAccreditationForm(configToken, payload);
      }
      setEditingForm(null);
      await load();
    } catch {
      setError("Erreur lors de l'enregistrement du formulaire.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async (formId) => {
    if (!window.confirm('Clôturer ce formulaire ? Il ne sera plus accessible aux nouveaux répondants.')) return;
    try {
      await closeAccreditationForm(configToken, formId);
      await load();
    } catch {
      setError('Erreur lors de la clôture du formulaire.');
    }
  };

  const handleRegenerate = async (formId) => {
    if (!window.confirm('Régénérer le lien public de ce formulaire ? L\'ancien lien cessera de fonctionner.')) return;
    try {
      await regenerateAccreditationFormPublicLink(configToken, formId);
      await load();
    } catch {
      setError('Erreur lors de la régénération du lien.');
    }
  };

  const handleCopy = async (formId, token) => {
    try {
      await navigator.clipboard.writeText(publicFormUrl(token));
      setCopiedFormId(formId);
      setTimeout(() => setCopiedFormId(''), 2000);
    } catch {
      window.prompt('Copiez ce lien :', publicFormUrl(token));
    }
  };

  const handleDecide = async (formId, responseId, status) => {
    setDecidingId(responseId);
    try {
      await updateAccreditationResponseStatus(configToken, formId, responseId, status);
      setResponsesByForm((prev) => ({
        ...prev,
        [formId]: prev[formId].map((r) => (r.id === responseId ? { ...r, status } : r)),
      }));
    } catch {
      setError('Erreur lors de la mise à jour du statut de la demande.');
    } finally {
      setDecidingId('');
    }
  };

  const toggleSelect = (formId, responseId) => {
    setSelectedByForm((prev) => {
      const current = prev[formId] || [];
      const next = current.includes(responseId)
        ? current.filter((id) => id !== responseId)
        : [...current, responseId];
      return { ...prev, [formId]: next };
    });
  };

  const toggleSelectAll = (formId, responseIds) => {
    setSelectedByForm((prev) => {
      const current = prev[formId] || [];
      const allSelected = responseIds.length > 0 && responseIds.every((id) => current.includes(id));
      return { ...prev, [formId]: allSelected ? [] : responseIds };
    });
  };

  const handleBulkDecide = async (formId, status) => {
    const responseIds = selectedByForm[formId] || [];
    if (responseIds.length === 0) return;
    setBulkDecidingFormId(formId);
    try {
      await bulkUpdateAccreditationResponseStatus(configToken, formId, responseIds, status);
      setResponsesByForm((prev) => ({
        ...prev,
        [formId]: prev[formId].map((r) => (responseIds.includes(r.id) ? { ...r, status } : r)),
      }));
      setSelectedByForm((prev) => ({ ...prev, [formId]: [] }));
    } catch {
      setError('Erreur lors de la mise à jour groupée du statut.');
    } finally {
      setBulkDecidingFormId('');
    }
  };

  // Dernière date d'export enregistrée pour un mode donné (ex. 'approved' ou 'wilout:approved') —
  // exportLog est trié du plus récent au plus ancien (accreditationExportLogService.listByProject),
  // donc le premier match est la bonne borne pour "nouveautés depuis mon dernier export CE filtre".
  const lastExportDateForMode = useCallback(
    (mode) => exportLog.find((e) => e.mode === mode)?.exportedAt || null,
    [exportLog]
  );

  // Export "brut" de toutes les réponses (filtrées par statut) de tous les formulaires — pas le
  // CSV format Wilout (réservé à l'outil interne, qui seul a les crédits cashless/codes-barres) :
  // juste ce que l'externe a déjà sous les yeux, pour son propre suivi. Inclut une colonne par
  // champ personnalisé distinct across tous les formulaires (Papa.unparse sans `columns`
  // explicite n'utiliserait que les clés de la 1ère ligne, perdant les champs des autres
  // formulaires) et journalise l'événement (mode + nombre de lignes, jamais le contenu).
  // `scope` ('all' | 'new') vient du choix fait dans la boîte de dialogue d'export : 'new' ne
  // garde que les réponses soumises après le dernier export journalisé pour ce même filtre.
  const handleExportAll = async (scope = 'all') => {
    const allForms = session?.forms || [];
    const filterFn = EXPORT_FILTER_TEST[exportStatusFilter] || EXPORT_FILTER_TEST.all;
    const since = scope === 'new' ? lastExportDateForMode(exportStatusFilter) : null;
    const customLabels = [...new Set(allForms.flatMap((f) => (f.config?.customFields || []).map((cf) => cf.label)))];
    const columns = ['Formulaire', "Type d'accès", 'Nom', 'Prénom', 'Email', 'Jours', 'Statut', 'Soumis le', ...customLabels];

    const rows = allForms.flatMap((f) => (responsesByForm[f.id] || [])
      .filter(filterFn)
      .filter((r) => !since || new Date(r.submittedAt) > new Date(since))
      .map((r) => {
        const row = {
          Formulaire: f.label, "Type d'accès": accessTypeLabelOf(r.accessTypeKey) || '', Nom: r.nom, 'Prénom': r.prenom, Email: r.email,
          Jours: (r.presentDays || []).join(', '),
          'Statut': STATUS_META[r.status]?.label || r.status,
          'Soumis le': r.submittedAt ? new Date(r.submittedAt).toLocaleString('fr-FR') : '',
        };
        for (const field of (f.config?.customFields || [])) {
          row[field.label] = r.customValues?.[field.key] || '';
        }
        return row;
      }));

    if (rows.length === 0) {
      setError(since ? 'Aucune nouvelle réponse depuis votre dernier export pour ce filtre.' : 'Aucune réponse à exporter pour ce filtre.');
      return;
    }
    setExportingAll(true);
    try {
      const csv = Papa.unparse(rows, { columns });
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(session?.name || 'accreditation').replace(/\s+/g, '_')}_reponses.csv`;
      a.click();
      URL.revokeObjectURL(url);

      try {
        const entry = await logAccreditationExport(configToken, { mode: exportStatusFilter, rowCount: rows.length });
        setExportLog((prev) => [entry, ...prev]);
      } catch {
        // La journalisation est secondaire — un échec ne doit pas faire croire à un échec d'export.
      }
    } finally {
      setExportingAll(false);
    }
  };

  // Export "format Wilout" : mêmes colonnes que l'export interne réservé admins/CDP (tarif,
  // codes-barres, crédits/unités cashless), SANS les champs personnalisés — contrairement à
  // handleExportAll ci-dessus, ce n'est pas un export de suivi mais un fichier prêt à réimporter
  // dans Wilout. Les lignes sont calculées côté serveur (accreditationRowBuilder, seule source de
  // vérité partagée avec l'outil interne) ; seul le CSV final (Papa.unparse) reste généré ici,
  // comme pour l'export brut. `scope` idem handleExportAll : 'new' ne garde que les réponses
  // soumises après le dernier export "format Wilout" journalisé pour ce même filtre.
  const handleExportWilout = async (scope = 'all') => {
    setExportingWilout(true);
    try {
      const wiloutMode = `wilout:${exportStatusFilter}`;
      const since = scope === 'new' ? lastExportDateForMode(wiloutMode) : null;
      const { columns, rows } = await getAccreditationWiloutExport(configToken, exportStatusFilter, since);
      if (rows.length === 0) {
        setError(since ? 'Aucune nouvelle réponse depuis votre dernier export pour ce filtre.' : 'Aucune réponse à exporter pour ce filtre.');
        return;
      }
      const csv = Papa.unparse(rows, { columns });
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(session?.name || 'accreditation').replace(/\s+/g, '_')}_wilout.csv`;
      a.click();
      URL.revokeObjectURL(url);

      try {
        const entry = await logAccreditationExport(configToken, { mode: wiloutMode, rowCount: rows.length });
        setExportLog((prev) => [entry, ...prev]);
      } catch {
        // La journalisation est secondaire — un échec ne doit pas faire croire à un échec d'export.
      }
    } catch {
      setError("Erreur lors de l'export au format Wilout.");
    } finally {
      setExportingWilout(false);
    }
  };

  const toggleGlobalSelect = (formId, id) => {
    setGlobalSelected((prev) => {
      const exists = prev.some((s) => s.formId === formId && s.id === id);
      return exists ? prev.filter((s) => !(s.formId === formId && s.id === id)) : [...prev, { formId, id }];
    });
  };

  const toggleGlobalSelectAll = (items) => {
    setGlobalSelected((prev) => {
      const allSelected = items.length > 0
        && items.every((it) => prev.some((s) => s.formId === it.formId && s.id === it.id));
      return allSelected ? [] : items;
    });
  };

  // Idem handleBulkDecide mais réparti sur plusieurs formulaires : bulkUpdateAccreditationResponseStatus
  // ne prend qu'un formId à la fois, donc on regroupe la sélection par formulaire avant d'appeler.
  const handleGlobalBulkDecide = async (status) => {
    if (globalSelected.length === 0) return;
    setGlobalBulkDeciding(true);
    try {
      const byForm = new Map();
      for (const { formId, id } of globalSelected) {
        if (!byForm.has(formId)) byForm.set(formId, []);
        byForm.get(formId).push(id);
      }
      await Promise.all([...byForm.entries()].map(
        ([formId, ids]) => bulkUpdateAccreditationResponseStatus(configToken, formId, ids, status)
      ));
      setResponsesByForm((prev) => {
        const next = { ...prev };
        for (const [formId, ids] of byForm.entries()) {
          next[formId] = (next[formId] || []).map((r) => (ids.includes(r.id) ? { ...r, status } : r));
        }
        return next;
      });
      setGlobalSelected([]);
    } catch {
      setError('Erreur lors de la mise à jour groupée du statut.');
    } finally {
      setGlobalBulkDeciding(false);
    }
  };

  // Noms des formulaires standard pas encore dans la liste : ce sont les tarifs Wilout existants, que
  // les types d'accès des chefs de pôle doivent reprendre à l'identique pour l'export.
  const standardFormNamesToAdd = [...new Set(
    (session?.forms || [])
      .filter((f) => f.mode !== 'pole' && f.status !== 'closed')
      .map((f) => f.label.trim())
      .filter((label) => label && !accessTypesDraft.some((t) => t.label.trim().toLowerCase() === label.toLowerCase()))
  )];
  const addFormNamesAsTypes = () =>
    setAccessTypesDraft((prev) => [...prev, ...standardFormNamesToAdd.map((label) => ({ label }))]);

  // Le brouillon n'est remplacé par la réponse du serveur que s'il n'a pas bougé entre-temps
  // (sinon on effacerait la frappe en cours).
  const daysAutosave = useAutosave({
    value: eventDaysDraft,
    saved: session?.eventDays,
    save: async (days) => {
      const { eventDays } = await updateAccreditationProjectEventDays(configToken, days);
      setSession((prev) => (prev ? { ...prev, eventDays } : prev));
      setEventDaysDraft((prev) => (JSON.stringify(prev) === JSON.stringify(days) ? eventDays : prev));
    },
  });
  const typesAutosave = useAutosave({
    value: accessTypesDraft,
    saved: session?.accessTypes,
    save: async (accessTypes) => {
      const result = await updateAccreditationAccessTypes(configToken, accessTypes);
      setSession((prev) => (prev ? { ...prev, accessTypes: result.accessTypes } : prev));
      setAccessTypesDraft((prev) => (JSON.stringify(prev) === JSON.stringify(accessTypes) ? result.accessTypes : prev));
    },
    // 409 : type encore utilisé par des demandes — on remet la liste du serveur, le message dit lesquels.
    onError: () => setAccessTypesDraft(session?.accessTypes || []),
  });
  const settingsStatuses = [daysAutosave.status, typesAutosave.status];
  let settingsStatus = 'idle';
  if (settingsStatuses.includes('error')) settingsStatus = 'error';
  else if (settingsStatuses.includes('saving')) settingsStatus = 'saving';
  else if (settingsStatuses.includes('saved')) settingsStatus = 'saved';

  const hideGuide = () => {
    setGuideHidden(true);
    try { window.localStorage.setItem(GUIDE_STORAGE_KEY, '1'); } catch { /* stockage indisponible : masqué pour cette visite seulement */ }
  };

  // Barre blanche avec le logo, pour les états chargement/erreur ; la vue principale a son propre en-tête.
  const Header = () => (
    <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dde3e8' }}>
      <Container sx={{ maxWidth: '1240px !important', display: 'flex', alignItems: 'center', height: 60 }}>
        <img src={wiloutLogo} alt="Wilout" style={{ height: 20 }} />
      </Container>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Header />
        <Container sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  if (error && !session) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Header />
        <Container sx={{ py: 8, maxWidth: '560px !important' }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  const allForms = session?.forms || [];
  const accessTypeLabelOf = (key) => (session?.accessTypes || []).find((t) => t.key === key)?.label || null;
  const forms = allForms
    .filter((f) => statusFilter === 'all' || (statusFilter === 'active' ? f.status !== 'closed' : f.status === 'closed'))
    .filter((f) => !query.trim() || f.label.toLowerCase().includes(query.trim().toLowerCase()));

  const activeCount = allForms.filter((f) => f.status !== 'closed').length;
  const totalResponses = allForms.reduce((s, f) => s + (responsesByForm[f.id]?.length || 0), 0);
  const totalPending = allForms.reduce(
    (s, f) => s + (responsesByForm[f.id] || []).filter((r) => r.status === 'pending').length, 0
  );
  const kpis = [
    { label: 'Formulaires actifs', value: `${activeCount} / ${allForms.length}`, color: '#002F36' },
    { label: 'Demandes reçues', value: totalResponses, color: '#002F36' },
    { label: 'À valider', value: totalPending, color: totalPending ? '#b54708' : '#007739' },
  ];
  const tabItems = [
    { label: 'Formulaires', count: allForms.length },
    { label: 'Toutes les réponses', count: totalResponses },
    { label: 'Historique des exports', count: exportLog.length },
    ...TOOL_VIEWS.map(({ label }) => ({ label })),
  ];
  const formMenuForm = formMenu ? allForms.find((f) => f.id === formMenu.formId) : null;

  // Vue "Toutes les réponses" : toutes les réponses de tous les formulaires à plat, chacune
  // porteuse de son formulaire d'origine (formLabel/formCustomFields) pour rester exploitable
  // hors du contexte de la carte formulaire (colonne dédiée + Détails par ligne).
  const combinedResponses = allForms.flatMap((f) => (responsesByForm[f.id] || []).map((r) => ({
    ...r, formId: f.id, formLabel: f.label, formCustomFields: f.config?.customFields || [],
    accessTypeLabel: accessTypeLabelOf(r.accessTypeKey),
  })));
  const filteredGlobalResponses = combinedResponses
    .filter((r) => globalStatusFilter === 'all' || r.status === globalStatusFilter)
    .filter((r) => {
      const q = globalQuery.trim().toLowerCase();
      if (!q) return true;
      return [r.nom, r.prenom, r.email, r.formLabel, r.accessTypeLabel].filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
  const globalDayGroups = groupResponsesByDay(filteredGlobalResponses);
  const globalHasCustomFields = filteredGlobalResponses.some((r) => (r.formCustomFields || []).length > 0);
  const globalColSpan = globalHasCustomFields ? 7 : 6;
  const globalItems = filteredGlobalResponses.map((r) => ({ formId: r.formId, id: r.id }));
  const allGlobalSelected = globalItems.length > 0
    && globalItems.every((it) => globalSelected.some((s) => s.formId === it.formId && s.id === it.id));
  const someGlobalSelected = globalSelected.length > 0 && !allGlobalSelected;

  // Mode journalisé correspondant au choix d'export en cours ('raw' → filtre de statut brut,
  // 'wilout' → préfixé, cf. handleExportWilout) — sert à retrouver la date du dernier export
  // avec ce même filtre pour proposer "nouveautés seulement" dans la boîte de dialogue.
  const exportScopeMode = exportScopeChoice === 'wilout' ? `wilout:${exportStatusFilter}` : exportStatusFilter;
  const exportScopeSince = exportScopeChoice ? lastExportDateForMode(exportScopeMode) : null;
  const runScopedExport = (scope) => {
    const kind = exportScopeChoice;
    setExportScopeChoice(null);
    if (kind === 'wilout') handleExportWilout(scope);
    else handleExportAll(scope);
  };

  const toolsEventName = session?.wiloutContext || session?.name;
  const renderTool = (view, onGenerated) => (view === 'access-codes' ? (
    <AccreditationAccessCodesPanel configToken={configToken} />
  ) : (
    <Suspense fallback={TOOLS_FALLBACK}>
      <ExternalAccreditationTools
        configToken={configToken}
        eventName={toolsEventName}
        view={view}
        defaultZones={(session?.accessTypes || []).map((a) => a.label)}
        defaultDays={(session?.eventDays || []).map((d) => String(d.label || '').toUpperCase()).filter(Boolean)}
        onGenerated={onGenerated}
      />
    </Suspense>
  ));

  // Espace « convertisseur seul » (formulaires désactivés par Wilout) : ni formulaires, ni
  // réponses — uniquement les outils fichiers (convertisseur, QR codes en réserve).
  if (session?.formsEnabled === false) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#EEF3F6' }}>
        <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dde3e8' }}>
          <Container sx={{ maxWidth: '1240px !important', px: { xs: 2, sm: 4 } }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ height: 60, borderBottom: '1px solid #eef1f4' }}>
              <img src={wiloutLogo} alt="Wilout" style={{ height: 20 }} />
              <Box sx={{ width: '1px', height: 20, bgcolor: '#dde3e8' }} />
              <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: '#5e646a' }}>Convertisseur d'accréditations</Typography>
            </Stack>
            <Box sx={{ pt: 3, pb: 2.25 }}>
              <Typography component="h1" sx={{
                fontFamily: 'Switzer, Inter, sans-serif', fontSize: 30, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1,
              }}>
                {session.wiloutContext || session.name}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#5e646a', mt: 0.75 }}>
                Convertissez vos listes au format Wilout et générez des QR codes en réserve par type d'accès et par journée.
              </Typography>
            </Box>
            <Tabs
              value={soloView} onChange={(e, v) => setSoloView(v)} variant="scrollable" scrollButtons="auto"
              TabIndicatorProps={{ sx: { height: 3, bgcolor: '#00B797' } }}
            >
              {TOOL_VIEWS.map(({ view, label }) => (
                <Tab key={view} value={view} label={label}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: 14, minHeight: 44, color: '#5e646a', '&.Mui-selected': { color: '#002F36' } }} />
              ))}
            </Tabs>
          </Container>
        </Box>
        <Container sx={{ maxWidth: '1240px !important', px: { xs: 2, sm: 4 }, pt: 3, pb: 7 }}>
          {renderTool(soloView, () => setSoloView('spares-view'))}
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#EEF3F6' }}>
      {/* En-tête : logo, événement, titre, indicateurs et onglets */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dde3e8' }}>
        <Container sx={{ maxWidth: '1240px !important', px: { xs: 2, sm: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ height: 60, borderBottom: '1px solid #eef1f4' }}>
            <img src={wiloutLogo} alt="Wilout" style={{ height: 20 }} />
            <Box sx={{ width: '1px', height: 20, bgcolor: '#dde3e8' }} />
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: '#5e646a' }}>{session?.wiloutContext || session?.name}</Typography>
            {eventDatesLabel(session?.eventDays) && (
              <Chip size="small" label={eventDatesLabel(session?.eventDays)} sx={{ bgcolor: '#eef3f6', color: '#5e646a', fontWeight: 600 }} />
            )}
          </Stack>

          <Stack direction="row" alignItems="flex-end" flexWrap="wrap" useFlexGap sx={{ gap: '16px 40px', pt: 3, pb: 2.25 }}>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <Typography component="h1" sx={{
                fontFamily: 'Switzer, Inter, sans-serif', fontSize: 30, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1,
              }}>
                Formulaires d'accréditation
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#5e646a', mt: 0.75 }}>
                Un formulaire par profil. Partagez le lien, validez les demandes, importez sur Wilout Online.
              </Typography>
            </Box>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              {kpis.map((k) => (
                <Box key={k.label}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#5e646a', whiteSpace: 'nowrap' }}>{k.label}</Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: k.color, whiteSpace: 'nowrap' }}>{k.value}</Typography>
                </Box>
              ))}
            </Stack>
          </Stack>

          <Tabs
            value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
            TabIndicatorProps={{ sx: { height: 3, bgcolor: '#00B797' } }}
          >
            {tabItems.map((t, i) => (
              <Tab
                key={t.label}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 14, minHeight: 44, color: '#5e646a', '&.Mui-selected': { color: '#002F36' } }}
                label={(
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{t.label}</span>
                    {t.count !== undefined && <Chip size="small" label={t.count} sx={{
                      height: 20, fontSize: 12, fontWeight: 700,
                      bgcolor: activeTab === i ? '#d6f5ee' : '#eef3f6', color: activeTab === i ? '#007a66' : '#5e646a',
                    }} />}
                  </Stack>
                )}
              />
            ))}
          </Tabs>
        </Container>
      </Box>

      <Container sx={{ maxWidth: '1240px !important', px: { xs: 2, sm: 4 }, pt: 3, pb: 7 }}>
        {!guideHidden && (
          <Box sx={{
            display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr) auto' }, gap: '1px', mb: 2.5,
            bgcolor: '#dde3e8', border: '1px solid #dde3e8', borderRadius: '12px', overflow: 'hidden',
          }}>
            {GUIDE_STEPS.map((step, i) => (
              <Stack key={step.title} direction="row" spacing={1.5} sx={{ p: '16px 18px', bgcolor: '#fff' }}>
                <Box sx={{
                  width: 28, height: 28, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '50%',
                  bgcolor: '#d6f5ee', color: '#007a66', fontSize: 13, fontWeight: 700,
                }}>
                  {i + 1}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{step.title}</Typography>
                  <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: '#5e646a' }}>{step.text}</Typography>
                </Box>
              </Stack>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1.5, bgcolor: '#fff' }}>
              <IconButton size="small" aria-label="Masquer le guide" onClick={hideGuide}><X size={16} /></IconButton>
            </Box>
          </Box>
        )}

        {error && <Alert severity="warning" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        {activeTab >= FORM_TAB_COUNT ? (
          renderTool(TOOL_VIEWS[activeTab - FORM_TAB_COUNT].view, () => setActiveTab(FORM_TAB_COUNT + SPARES_VIEW_INDEX))
        ) : activeTab === 2 ? (
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Filtre</TableCell>
                    <TableCell align="right">Lignes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exportLog.map((entry, i) => (
                    <TableRow key={i}>
                      <TableCell>{new Date(entry.exportedAt).toLocaleString('fr-FR')}</TableCell>
                      <TableCell>{exportModeLabel(entry.mode)}</TableCell>
                      <TableCell align="right">{entry.rowCount}</TableCell>
                    </TableRow>
                  ))}
                  {exportLog.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        Aucun export effectué pour l'instant.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : activeTab === 1 ? (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Statut</InputLabel>
                <Select label="Statut" value={globalStatusFilter} onChange={(e) => setGlobalStatusFilter(e.target.value)}>
                  <MenuItem value="all">Tous les statuts</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="approved">Validées</MenuItem>
                  <MenuItem value="rejected">Refusées</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small" placeholder="Rechercher (nom, email, formulaire)…" value={globalQuery}
                onChange={(e) => setGlobalQuery(e.target.value)}
                sx={{ width: { xs: '100%', sm: 280 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> }}
              />
              <Box sx={{ flex: '1 1 auto' }} />
              <Typography variant="caption" color="text.secondary">
                {filteredGlobalResponses.length} réponse{filteredGlobalResponses.length > 1 ? 's' : ''}
              </Typography>
            </Stack>

            {globalSelected.length > 0 && (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5, px: 1.5, py: 1, bgcolor: 'action.selected', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {globalSelected.length} sélectionnée{globalSelected.length > 1 ? 's' : ''}
                </Typography>
                <Button
                  size="small" variant="contained" color="success"
                  disabled={globalBulkDeciding}
                  onClick={() => handleGlobalBulkDecide('approved')}
                >
                  Valider la sélection ({globalSelected.length})
                </Button>
                <Button
                  size="small" variant="contained" color="error"
                  disabled={globalBulkDeciding}
                  onClick={() => handleGlobalBulkDecide('rejected')}
                >
                  Refuser la sélection ({globalSelected.length})
                </Button>
              </Stack>
            )}

            {filteredGlobalResponses.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', fontSize: 13, color: 'text.secondary', border: '1px dashed', borderColor: 'divider', borderRadius: 2.5 }}>
                Aucune réponse ne correspond à ce filtre.
              </Box>
            ) : (
              <TableContainer sx={{ border: '1px solid #e9edf1', borderRadius: 2.5, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small" indeterminate={someGlobalSelected} checked={allGlobalSelected}
                          onChange={() => toggleGlobalSelectAll(globalItems)}
                        />
                      </TableCell>
                      <TableCell>Formulaire</TableCell>
                      <TableCell>Personne</TableCell>
                      <TableCell>Jours</TableCell>
                      {globalHasCustomFields && <TableCell>Détails</TableCell>}
                      <TableCell>Statut</TableCell>
                      <TableCell align="right">Décision</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {globalDayGroups.map(({ dayKey, groups }) => (
                      <React.Fragment key={dayKey}>
                        <TableRow>
                          <TableCell colSpan={globalColSpan} sx={{ bgcolor: '#f1f5f8', py: 0.5, border: 0 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', color: 'text.secondary' }}>
                              {dayLabelOf(dayKey)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {groups.map((group) => group.map((r, memberIdx) => {
                          const meta = STATUS_META[r.status] || STATUS_META.pending;
                          const inGroup = group.length > 1;
                          const category = categoryVisual(r.accessTypeLabel || r.formLabel);
                          const isSelected = globalSelected.some((s) => s.formId === r.formId && s.id === r.id);
                          return (
                            <TableRow
                              key={`${r.formId}-${r.id}`} selected={isSelected} hover
                              sx={inGroup ? { bgcolor: 'rgba(0,190,158,0.05)' } : undefined}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox size="small" checked={isSelected} onChange={() => toggleGlobalSelect(r.formId, r.id)} />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small" icon={<category.icon size={12} />} label={r.accessTypeLabel || r.formLabel}
                                  sx={{ bgcolor: category.bg, color: category.fg, fontWeight: 700, height: 22, '& .MuiChip-icon': { color: category.fg } }}
                                />
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                  <Box sx={{
                                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                                    bgcolor: '#e9edf1', color: '#5e646a', display: 'grid', placeItems: 'center',
                                    fontSize: 12, fontWeight: 700,
                                  }}>
                                    {initialsOf(r.nom, r.prenom)}
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                                        {[r.prenom, r.nom].filter(Boolean).join(' ')}
                                      </Typography>
                                      {inGroup && memberIdx === 0 && (
                                        <Chip size="small" label={`Groupe de ${group.length}`} sx={{ height: 16, fontSize: 10, fontWeight: 700 }} />
                                      )}
                                    </Stack>
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                                      {r.email || '—'}
                                    </Typography>
                                    {r.cashless && (
                                      <Typography sx={{ fontSize: 12, color: '#007773', fontWeight: 600 }} noWrap>
                                        {cashlessSummary(r.cashless)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell>{(r.presentDays || []).join(', ') || '—'}</TableCell>
                              {globalHasCustomFields && (
                                <TableCell>
                                  {(r.formCustomFields || []).map((field) => {
                                    const val = r.customValues?.[field.key];
                                    return val ? (
                                      <Typography key={field.key} sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                        <b>{field.label} :</b> {val}
                                      </Typography>
                                    ) : null;
                                  })}
                                </TableCell>
                              )}
                              <TableCell>
                                <Chip size="small" label={meta.label} sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700, height: 22 }} />
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title="Valider">
                                    <span>
                                      <IconButton
                                        size="small" disabled={decidingId === r.id}
                                        onClick={() => handleDecide(r.formId, r.id, 'approved')}
                                        sx={{ color: '#007739', bgcolor: r.status === 'approved' ? '#d0f4e3' : '#eefaf1' }}
                                      >
                                        <Check size={14} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Refuser">
                                    <span>
                                      <IconButton
                                        size="small" disabled={decidingId === r.id}
                                        onClick={() => handleDecide(r.formId, r.id, 'rejected')}
                                        sx={{ color: '#970000', bgcolor: r.status === 'rejected' ? '#ffedea' : '#fff3f1' }}
                                      >
                                        <X size={14} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        }))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained" startIcon={<Plus size={16} />} onClick={() => setEditingForm({})}
                  sx={{ background: BRAND_GRADIENT, borderBottom: '4px solid #00be9e', boxShadow: '0 8px 20px -2px rgba(0,37,43,0.36)', whiteSpace: 'nowrap' }}
                >
                  Nouveau formulaire
                </Button>
                <Stack direction="row" sx={{ p: '3px', gap: '2px', bgcolor: '#fff', border: '1px solid #dde3e8', borderRadius: '10px' }}>
                  {STATUS_FILTERS.map((fi) => (
                    <Button
                      key={fi.value} onClick={() => setStatusFilter(fi.value)}
                      sx={{
                        height: 32, px: 1.5, minWidth: 0, fontSize: 13, fontWeight: 600, borderRadius: '7px', whiteSpace: 'nowrap',
                        color: statusFilter === fi.value ? '#fff' : '#5e646a',
                        bgcolor: statusFilter === fi.value ? '#002F36' : 'transparent',
                        '&:hover': { bgcolor: statusFilter === fi.value ? '#002F36' : '#eef3f6' },
                      }}
                    >
                      {fi.label}
                    </Button>
                  ))}
                </Stack>
                <Box sx={{ flex: '1 1 auto' }} />
                <TextField
                  size="small" placeholder="Rechercher…" value={query} onChange={(e) => setQuery(e.target.value)}
                  sx={{ flex: '0 1 220px', minWidth: 160, bgcolor: '#fff', borderRadius: '6px' }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> }}
                />
                <Button
                  variant="outlined" color="inherit" disabled={totalResponses === 0 || exportingAll || exportingWilout}
                  startIcon={(exportingAll || exportingWilout) ? <CircularProgress size={14} /> : <FileDown size={16} />}
                  endIcon={<ChevronDown size={12} />}
                  onClick={(e) => setExportAnchor(e.currentTarget)}
                  sx={{ bgcolor: '#fff', borderColor: '#dde3e8', color: '#002F36', fontWeight: 600, borderRadius: '15px', whiteSpace: 'nowrap' }}
                >
                  Exporter
                </Button>
              </Stack>

              <Box sx={{ bgcolor: '#fff', border: '1px solid #dde3e8', borderRadius: '12px' }}>
                <Box sx={{
                  display: { xs: 'none', md: 'grid' }, gridTemplateColumns: FORM_ROW_COLUMNS, gap: 2, alignItems: 'center',
                  px: 2.25, py: 1.25, fontSize: 12, fontWeight: 600, color: '#5e646a', borderBottom: '1px solid #e9edf1',
                }}>
                  <span>Formulaire</span><span>Statut</span><span>Demandes</span><span style={{ width: 236 }}>Lien de partage</span>
                </Box>
              {forms.map((f) => {
                const responses = responsesByForm[f.id] || [];
                const pendingCount = responses.filter((r) => r.status === 'pending').length;
                const expanded = expandedFormId === f.id;
                const category = categoryVisual(f.label);
                const responseIds = responses.map((r) => r.id);
                const selected = selectedByForm[f.id] || [];
                const allSelected = responseIds.length > 0 && responseIds.every((id) => selected.includes(id));
                const someSelected = selected.length > 0 && !allSelected;
                const customFields = f.config?.customFields || [];
                const hasCustomFields = customFields.length > 0;
                const dayGroups = groupResponsesByDay(responses);
                const colSpan = hasCustomFields ? 6 : 5;

                return (
                  <Box key={f.id} sx={{ borderBottom: '1px solid #eef1f4', '&:last-of-type': { borderBottom: 0 } }}>
                    <Box sx={{
                      display: 'grid', gridTemplateColumns: { xs: '1fr', md: FORM_ROW_COLUMNS }, gap: 2, alignItems: 'center',
                      px: 2.25, py: 1.75, transition: 'background 150ms ease', '&:hover': { bgcolor: '#fafcfd' },
                    }}>
                      <Stack
                        direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, cursor: 'pointer' }}
                        onClick={() => setExpandedFormId(expanded ? '' : f.id)}
                      >
                        <Box sx={{
                          width: 40, height: 40, flexShrink: 0, borderRadius: '10px',
                          display: 'grid', placeItems: 'center', bgcolor: category.bg, color: category.fg,
                        }}>
                          <category.icon size={18} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: 15, fontWeight: 600 }}>{f.label}</Typography>
                          <Typography noWrap sx={{ fontSize: 12, color: '#5e646a' }}>
                            {[f.mode === 'pole' && 'Chef de pôle', f.status === 'closed' ? 'Fermé aux nouvelles demandes' : 'Ouvert aux demandes']
                              .filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        {expanded ? <ChevronDown size={16} color="#5e646a" /> : <ChevronRight size={16} color="#5e646a" />}
                      </Stack>

                      <Box>
                        <Chip
                          size="small"
                          label={f.status === 'closed' ? 'Clôturé' : 'Actif'}
                          sx={{
                            height: 24, fontWeight: 600,
                            bgcolor: f.status === 'closed' ? '#eef3f6' : '#d0f4e3',
                            color: f.status === 'closed' ? '#5e646a' : '#007739',
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {responses.length === 0 ? 'Aucune' : `${responses.length} reçue${responses.length > 1 ? 's' : ''}`}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: pendingCount ? '#b54708' : '#5e646a' }}>
                          {pendingCount ? `${pendingCount} à valider` : (responses.length ? 'Toutes traitées' : '—')}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title={publicFormUrl(f.token)} describeChild>
                          <Button
                            onClick={() => handleCopy(f.id, f.token)}
                            startIcon={copiedFormId === f.id ? <Check size={14} /> : <Link2 size={14} />}
                            sx={{
                              width: 148, height: 34, justifyContent: 'flex-start', px: 1.5, fontSize: 13, fontWeight: 600,
                              whiteSpace: 'nowrap', border: '1px solid', borderRadius: '8px',
                              color: copiedFormId === f.id ? '#007739' : '#002F36',
                              bgcolor: copiedFormId === f.id ? '#e3f4ea' : '#fff',
                              borderColor: copiedFormId === f.id ? '#a6dcbc' : '#dde3e8',
                              '&:hover': { borderColor: '#00B797', bgcolor: '#fff' },
                            }}
                          >
                            {copiedFormId === f.id ? 'Lien copié' : 'Copier le lien'}
                          </Button>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small" aria-label="Modifier" onClick={() => setEditingForm(f)}
                            sx={{ width: 34, height: 34, border: '1px solid #dde3e8', borderRadius: '8px', color: '#002F36' }}
                          >
                            <Pencil size={13} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Plus d'actions">
                          <IconButton
                            size="small" aria-label="Plus d'actions" onClick={(e) => setFormMenu({ anchorEl: e.currentTarget, formId: f.id })}
                            sx={{ width: 34, height: 34, color: '#5e646a' }}
                          >
                            <MoreVertical size={16} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    <Collapse in={expanded}>
                      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fbfcfd', px: { xs: 1.5, sm: 2.5 }, py: 2.25 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.5 }}>
                          Demandes reçues
                        </Typography>

                        {responses.length === 0 ? (
                          <Box sx={{ p: 2, textAlign: 'center', fontSize: 13, color: 'text.secondary', border: '1px dashed', borderColor: 'divider', borderRadius: 2.5 }}>
                            Aucune demande reçue pour l'instant.
                          </Box>
                        ) : (
                          <>
                            {selected.length > 0 && (
                              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5, px: 1.5, py: 1, bgcolor: 'action.selected', borderRadius: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {selected.length} sélectionnée{selected.length > 1 ? 's' : ''}
                                </Typography>
                                <Button
                                  size="small" variant="contained" color="success"
                                  disabled={bulkDecidingFormId === f.id}
                                  onClick={() => handleBulkDecide(f.id, 'approved')}
                                >
                                  Valider la sélection ({selected.length})
                                </Button>
                                <Button
                                  size="small" variant="contained" color="error"
                                  disabled={bulkDecidingFormId === f.id}
                                  onClick={() => handleBulkDecide(f.id, 'rejected')}
                                >
                                  Refuser la sélection ({selected.length})
                                </Button>
                              </Stack>
                            )}
                            <TableContainer sx={{ border: '1px solid #e9edf1', borderRadius: 2.5, overflowX: 'auto' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        size="small" indeterminate={someSelected} checked={allSelected}
                                        onChange={() => toggleSelectAll(f.id, responseIds)}
                                      />
                                    </TableCell>
                                    <TableCell>Personne</TableCell>
                                    <TableCell>Jours</TableCell>
                                    {hasCustomFields && <TableCell>Détails</TableCell>}
                                    <TableCell>Statut</TableCell>
                                    <TableCell align="right">Décision</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dayGroups.map(({ dayKey, groups }) => (
                                    <React.Fragment key={dayKey}>
                                      <TableRow>
                                        <TableCell colSpan={colSpan} sx={{ bgcolor: '#f1f5f8', py: 0.5, border: 0 }}>
                                          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', color: 'text.secondary' }}>
                                            {dayLabelOf(dayKey)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                      {groups.map((group) => group.map((r, memberIdx) => {
                                        const meta = STATUS_META[r.status] || STATUS_META.pending;
                                        const inGroup = group.length > 1;
                                        return (
                                          <TableRow
                                            key={r.id} selected={selected.includes(r.id)} hover
                                            sx={inGroup ? { bgcolor: 'rgba(0,190,158,0.05)' } : undefined}
                                          >
                                            <TableCell padding="checkbox">
                                              <Checkbox size="small" checked={selected.includes(r.id)} onChange={() => toggleSelect(f.id, r.id)} />
                                            </TableCell>
                                            <TableCell>
                                              <Stack direction="row" spacing={1.25} alignItems="center">
                                                <Box sx={{
                                                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                                                  bgcolor: '#e9edf1', color: '#5e646a', display: 'grid', placeItems: 'center',
                                                  fontSize: 12, fontWeight: 700,
                                                }}>
                                                  {initialsOf(r.nom, r.prenom)}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                  <Stack direction="row" spacing={0.75} alignItems="center">
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                                                      {[r.prenom, r.nom].filter(Boolean).join(' ')}
                                                    </Typography>
                                                    {inGroup && memberIdx === 0 && (
                                                      <Chip size="small" label={`Groupe de ${group.length}`} sx={{ height: 16, fontSize: 10, fontWeight: 700 }} />
                                                    )}
                                                  </Stack>
                                                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                                                    {[accessTypeLabelOf(r.accessTypeKey), r.email].filter(Boolean).join(' · ') || '—'}
                                                  </Typography>
                                                  {r.cashless && (
                                                    <Typography sx={{ fontSize: 12, color: '#007773', fontWeight: 600 }} noWrap>
                                                      {cashlessSummary(r.cashless)}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Stack>
                                            </TableCell>
                                            <TableCell>{(r.presentDays || []).join(', ') || '—'}</TableCell>
                                            {hasCustomFields && (
                                              <TableCell>
                                                {customFields.map((field) => {
                                                  const val = r.customValues?.[field.key];
                                                  return val ? (
                                                    <Typography key={field.key} sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                      <b>{field.label} :</b> {val}
                                                    </Typography>
                                                  ) : null;
                                                })}
                                              </TableCell>
                                            )}
                                            <TableCell>
                                              <Chip size="small" label={meta.label} sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700, height: 22 }} />
                                            </TableCell>
                                            <TableCell align="right">
                                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Valider">
                                                  <span>
                                                    <IconButton
                                                      size="small" disabled={decidingId === r.id}
                                                      onClick={() => handleDecide(f.id, r.id, 'approved')}
                                                      sx={{ color: '#007739', bgcolor: r.status === 'approved' ? '#d0f4e3' : '#eefaf1' }}
                                                    >
                                                      <Check size={14} />
                                                    </IconButton>
                                                  </span>
                                                </Tooltip>
                                                <Tooltip title="Refuser">
                                                  <span>
                                                    <IconButton
                                                      size="small" disabled={decidingId === r.id}
                                                      onClick={() => handleDecide(f.id, r.id, 'rejected')}
                                                      sx={{ color: '#970000', bgcolor: r.status === 'rejected' ? '#ffedea' : '#fff3f1' }}
                                                    >
                                                      <X size={14} />
                                                    </IconButton>
                                                  </span>
                                                </Tooltip>
                                              </Stack>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }))}
                                    </React.Fragment>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}

              {forms.length === 0 && (
                <Box sx={{ p: 5, textAlign: 'center', fontSize: 14, color: '#5e646a' }}>
                  {query.trim() || statusFilter !== 'all'
                    ? 'Aucun formulaire ne correspond.'
                    : "Aucun formulaire pour l'instant."}
                </Box>
              )}
              </Box>
            </Box>

            <AccreditationSettingsAside
              days={eventDaysDraft} onDaysChange={setEventDaysDraft}
              types={accessTypesDraft} onTypesChange={setAccessTypesDraft}
              importCount={standardFormNamesToAdd.length} onImportNames={addFormNamesAsTypes}
              status={settingsStatus} error={daysAutosave.error || typesAutosave.error}
            />
          </Box>
        )}
      </Container>

      {/* Menu « Plus d'actions » d'un formulaire */}
      <Menu
        anchorEl={formMenu?.anchorEl} open={Boolean(formMenu)} onClose={() => setFormMenu(null)}
        PaperProps={{ sx: { width: 220, borderRadius: '10px', border: '1px solid #dde3e8' } }}
      >
        {formMenuForm && formMenuForm.status !== 'closed' && (
          <MenuItem onClick={() => { const id = formMenuForm.id; setFormMenu(null); handleClose(id); }} sx={{ gap: 1.25, fontSize: 13 }}>
            <Lock size={14} color="#5e646a" />Clôturer le formulaire
          </MenuItem>
        )}
        {formMenuForm && (
          <MenuItem onClick={() => { const id = formMenuForm.id; setFormMenu(null); handleRegenerate(id); }} sx={{ gap: 1.25, fontSize: 13 }}>
            <RefreshCw size={14} color="#5e646a" />Régénérer le lien
          </MenuItem>
        )}
      </Menu>

      {/* Menu « Exporter » : demandes à inclure + format */}
      <Popover
        open={Boolean(exportAnchor)} anchorEl={exportAnchor} onClose={() => setExportAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 320, p: 1.75, mt: 0.75, borderRadius: '12px', border: '1px solid #dde3e8' } }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#5e646a', mb: 1 }}>Demandes à inclure</Typography>
        <ToggleButtonGroup
          exclusive fullWidth size="small" value={exportStatusFilter} sx={{ mb: 1.5 }}
          onChange={(e, v) => { if (v) setExportStatusFilter(v); }}
        >
          {EXPORT_FILTERS.map((f) => (
            <ToggleButton key={f.value} value={f.value} sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600 }}>
              {EXPORT_FILTER_SHORT[f.value]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <MenuItem onClick={() => { setExportAnchor(null); setExportScopeChoice('raw'); }} sx={{ gap: 1.5, borderRadius: '8px', alignItems: 'flex-start' }}>
          <Box sx={{ width: 32, height: 32, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '8px', bgcolor: '#e3f4ea', color: '#007739' }}>
            <FileDown size={16} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Tableur</Typography>
            <Typography sx={{ fontSize: 12, color: '#5e646a', whiteSpace: 'normal' }}>Toutes les colonnes des formulaires</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => { setExportAnchor(null); setExportScopeChoice('wilout'); }} sx={{ gap: 1.5, borderRadius: '8px', alignItems: 'flex-start' }}>
          <Box sx={{ width: 32, height: 32, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '8px', bgcolor: '#d6f5ee', color: '#007a66' }}>
            <FileDown size={16} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Format Wilout Online</Typography>
            <Typography sx={{ fontSize: 12, color: '#5e646a', whiteSpace: 'normal' }}>Prêt à importer : accès, tarifs, produits</Typography>
          </Box>
        </MenuItem>
      </Popover>

      {/* Éditeur en modale : le bloc apparaissait auparavant en bas de page, hors champ de vision
          quand on cliquait "Modifier" sur un formulaire loin dans la liste. */}
      <Dialog
        open={Boolean(editingForm)} onClose={() => setEditingForm(null)}
        fullWidth maxWidth="md" scroll="paper"
      >
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {editingForm && (
            <AccreditationFormEditor
              // session.forms est la forme serveur { id, label, config: {...} } — AccreditationFormEditor
              // attend un objet plat (label + champs de config au même niveau), d'où l'aplatissement ici.
              initialForm={editingForm.id ? { id: editingForm.id, label: editingForm.label, mode: editingForm.mode, ...editingForm.config } : null}
              onSave={handleSaveForm}
              onCancel={() => setEditingForm(null)}
              saving={saving}
              projectEventDays={session?.eventDays || []}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Choix de la portée d'export — "tout depuis le début" ou "nouveautés seulement" (basé sur
          accreditation_export_log, le dernier export journalisé pour ce même filtre de statut). */}
      <Dialog open={Boolean(exportScopeChoice)} onClose={() => setExportScopeChoice(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>Que voulez-vous exporter ?</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Filtre actuel : {EXPORT_FILTERS.find((f) => f.value === exportStatusFilter)?.label}
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="outlined" color="inherit" fullWidth
              sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.25, textTransform: 'none' }}
              onClick={() => runScopedExport('all')}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Tout depuis le début</Typography>
                <Typography variant="caption" color="text.secondary">Toutes les réponses correspondant à ce filtre.</Typography>
              </Box>
            </Button>
            <Tooltip title={exportScopeSince ? '' : 'Aucun export précédent avec ce filtre'} disableHoverListener={!!exportScopeSince}>
              <span>
                <Button
                  variant="outlined" color="inherit" fullWidth disabled={!exportScopeSince}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.25, textTransform: 'none' }}
                  onClick={() => runScopedExport('new')}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Nouveautés seulement</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {exportScopeSince
                        ? `Réponses soumises depuis votre dernier export (${new Date(exportScopeSince).toLocaleString('fr-FR')})`
                        : 'Aucun export précédent avec ce filtre'}
                    </Typography>
                  </Box>
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AccreditationConfigApp;
